import { useState, type CSSProperties, type FC, type FormEvent } from 'react';
import type { ClientNotebookNote } from '../types';

interface ClientNotebookOverlayProps {
  notes: ClientNotebookNote[];
  isOpen: boolean;
  onClose: () => void;
  onCreateNote: (payload: Partial<ClientNotebookNote>) => Promise<void>;
  onUpdateNote: (id: string, payload: Partial<ClientNotebookNote>) => Promise<void>;
  onDeleteNote: (id: string) => Promise<void>;
}

/** Academic notebook — cab-overlay chrome + cabinet-* body. */
export const ClientNotebookOverlay: FC<ClientNotebookOverlayProps> = ({
  notes,
  isOpen,
  onClose,
  onCreateNote,
  onUpdateNote,
  onDeleteNote,
}) => {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(notes[0]?.id || '');
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editTags, setEditTags] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  if (!isOpen) return null;

  const filtered = notes.filter(
    (n) =>
      n.title.toLowerCase().includes(search.toLowerCase()) ||
      n.content.toLowerCase().includes(search.toLowerCase()) ||
      (n.tags && n.tags.toLowerCase().includes(search.toLowerCase())),
  );

  const selected = notes.find((n) => n.id === selectedId) || filtered[0] || notes[0];

  const startCreate = () => {
    setIsCreating(true);
    setIsEditing(false);
    setEditTitle('');
    setEditContent('');
    setEditTags('');
    setIsPinned(false);
  };

  const startEdit = (note: ClientNotebookNote) => {
    setIsCreating(false);
    setIsEditing(true);
    setEditTitle(note.title);
    setEditContent(note.content);
    setEditTags(note.tags || '');
    setIsPinned(note.is_pinned);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editTitle.trim()) return;

    if (isCreating) {
      await onCreateNote({
        title: editTitle.trim(),
        content: editContent,
        tags: editTags,
        is_pinned: isPinned,
      });
      setIsCreating(false);
    } else if (isEditing && selected) {
      await onUpdateNote(selected.id, {
        title: editTitle.trim(),
        content: editContent,
        tags: editTags,
        is_pinned: isPinned,
      });
      setIsEditing(false);
    }
  };

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true" aria-labelledby="notebook-title">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Блокнот клиента</small>
              <h2 id="notebook-title">Академический блокнот</h2>
              <p>Рабочие гипотезы, цитаты, регламенты журналов и научные заметки</p>
            </div>
          </div>
          <div className="cab-top-actions">
            <button type="button" className="cab-btn primary" onClick={startCreate}>
              + Новая заметка
            </button>
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              ✕
            </button>
          </div>
        </div>

        <div
          className="cab-body"
          style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 0.85fr) minmax(0, 1.15fr)', gap: 14 }}
        >
          <aside className="cabinet-panel">
            <header>
              <div>
                <h3>Заметки</h3>
                <p>{filtered.length} найдено</p>
              </div>
            </header>
            <div className="cabinet-panel-body">
              <input
                className="cabinet-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск по заметкам и тегам..."
                style={{ width: '100%', marginBottom: 12 }}
              />
              <div className="cabinet-stack">
                {filtered.length === 0 ? (
                  <div className="cabinet-note">Заметок не найдено</div>
                ) : (
                  filtered.map((note) => {
                    const isSel = selected?.id === note.id && !isCreating;
                    return (
                      <button
                        key={note.id}
                        type="button"
                        className="cabinet-card"
                        style={
                          {
                            '--accent': isSel ? '#ff7b65' : '#8faebb',
                            textAlign: 'left',
                            cursor: 'pointer',
                            width: '100%',
                            color: 'inherit',
                            font: 'inherit',
                          } as CSSProperties
                        }
                        onClick={() => {
                          setSelectedId(note.id);
                          setIsCreating(false);
                          setIsEditing(false);
                        }}
                      >
                        <div className="cabinet-actions" style={{ marginBottom: 4 }}>
                          <strong style={{ fontSize: 13 }}>{note.title}</strong>
                          {note.is_pinned ? (
                            <span
                              className="cabinet-badge"
                              style={{ '--badge': '#ff7b65' } as CSSProperties}
                            >
                              Закреплено
                            </span>
                          ) : null}
                        </div>
                        <p style={{ margin: 0, color: '#8faebb', fontSize: 12, lineHeight: 1.4 }}>
                          {note.content}
                        </p>
                        {note.tags ? (
                          <p style={{ margin: '8px 0 0', color: '#ff7b65', fontSize: 11 }}>
                            #{note.tags.split(',').join(' #')}
                          </p>
                        ) : null}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </aside>

          <main className="cabinet-stack">
            {isCreating || isEditing ? (
              <form
                onSubmit={handleSave}
                className="cabinet-card"
                style={{ '--accent': '#ff7b65' } as CSSProperties}
              >
                <div className="cabinet-actions" style={{ marginBottom: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 16 }}>
                    {isCreating ? 'Создание заметки' : 'Редактирование заметки'}
                  </h3>
                  <span className="cabinet-spacer" />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                    <input
                      type="checkbox"
                      checked={isPinned}
                      onChange={(e) => setIsPinned(e.target.checked)}
                    />
                    Закрепить в начале
                  </label>
                </div>
                <div className="cabinet-stack">
                  <label className="cabinet-field">
                    <span>Заголовок</span>
                    <input
                      className="cabinet-input"
                      type="text"
                      required
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder="Заголовок заметки..."
                    />
                  </label>
                  <label className="cabinet-field">
                    <span>Теги</span>
                    <input
                      className="cabinet-input"
                      type="text"
                      value={editTags}
                      onChange={(e) => setEditTags(e.target.value)}
                      placeholder="Теги через запятую (например: Scopus, Методология, Грант)"
                    />
                  </label>
                  <label className="cabinet-field">
                    <span>Текст</span>
                    <textarea
                      className="cabinet-textarea"
                      rows={12}
                      required
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      placeholder="Текст научной заметки, ссылки, цитаты..."
                    />
                  </label>
                  <div className="cabinet-actions" style={{ justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="cabinet-btn"
                      onClick={() => {
                        setIsCreating(false);
                        setIsEditing(false);
                      }}
                    >
                      Отмена
                    </button>
                    <button type="submit" className="cabinet-btn primary">
                      Сохранить
                    </button>
                  </div>
                </div>
              </form>
            ) : selected ? (
              <article className="cabinet-card" style={{ '--accent': '#ff7b65' } as CSSProperties}>
                <div className="cabinet-actions">
                  <div>
                    <div className="cabinet-actions" style={{ marginBottom: 6 }}>
                      <h3 style={{ margin: 0, fontSize: 18 }}>{selected.title}</h3>
                      {selected.is_pinned ? (
                        <span
                          className="cabinet-badge"
                          style={{ '--badge': '#ff7b65' } as CSSProperties}
                        >
                          Закреплено
                        </span>
                      ) : null}
                    </div>
                    {selected.tags ? (
                      <div className="cabinet-actions">
                        {selected.tags.split(',').map((t, idx) => (
                          <span
                            key={idx}
                            className="cabinet-badge"
                            style={{ '--badge': '#8faebb' } as CSSProperties}
                          >
                            #{t.trim()}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <span className="cabinet-spacer" />
                  <button
                    type="button"
                    className="cabinet-btn"
                    onClick={() => startEdit(selected)}
                  >
                    Редактировать
                  </button>
                  <button
                    type="button"
                    className="cabinet-btn"
                    onClick={() => void onDeleteNote(selected.id)}
                  >
                    Удалить
                  </button>
                </div>
                <div
                  style={{
                    marginTop: 14,
                    whiteSpace: 'pre-wrap',
                    color: '#c5d6df',
                    fontSize: 13,
                    lineHeight: 1.6,
                  }}
                >
                  {selected.content}
                </div>
                <p style={{ margin: '14px 0 0', color: '#8faebb', fontSize: 11 }}>
                  Последнее обновление: {new Date(selected.updated_at).toLocaleString()}
                </p>
              </article>
            ) : (
              <div className="cabinet-note">Выберите или создайте заметку</div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};
